import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import {
  ArrowLeft,
  Bell,
  ChevronDown,
  Cloud,
  LayoutGrid,
  LoaderCircle,
  Menu,
  PanelRight,
  Search,
} from 'lucide-react';
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { ConversationBranchChip } from '@/components/desktop-shell/branch-actions-menu';
import { ParentLink, useChildConversation } from '@/components/desktop-shell/child-conversation';
import { UsageRingGlyph, usageRatio } from '@/components/desktop-shell/composer-usage-indicator';
import type { ConversationHeaderProps } from '@/components/desktop-shell/conversation-header';
import {
  ConversationMenu,
  useConversationMenu,
  useProjectMenu,
} from '@/components/desktop-shell/conversation-menu';
import { useThreadSummaryRender } from '@/components/desktop-shell/thread-summary';
import { useShellNavigation, useSidebarWaiting } from '@/components/sidebar/shell-navigation';
import { useAssistantOfConversation } from '@/features/assistants/use-assistant';
import { useNotifications } from '@/features/notifications/notifications';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { panelTabListSlot, panelTitleSlot } from '@/lib/desktop-shell/panel-screen-slots';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { DRAWER_WIDTH } from './mobile-drawer';
import { MobileSheetHeader } from './mobile-sheet-header';
import { releasesSwipe, useHorizontalDrag } from './use-horizontal-drag';
import { EDGE_WIDTH, useSwipeBack } from './use-swipe-back';
import { useSwipeReveal } from './use-swipe-reveal';

/**
 * Drags the conversation aside from the screen's left edge to show the drawer under it, like a
 * phone's own apps: released far enough (or flicked), the drawer opens.
 */
function useSwipeOpenDrawer(
  ref: RefObject<HTMLElement | null>,
  onOpen: () => void,
  enabled: boolean,
) {
  const open = useRef(onOpen);
  open.current = onOpen;
  const settle = () => {
    const screen = ref.current;
    if (screen === null) return;
    screen.style.transition = '';
    screen.style.transform = '';
  };
  useHorizontalDrag(ref, {
    enabled,
    accepts: (_event, x) => x <= EDGE_WIDTH,
    allows: (dx) => dx > 0,
    onDrag: (dx) => {
      const screen = ref.current;
      if (screen === null) return;
      screen.style.transition = 'none';
      screen.style.transform = `translateX(${Math.min(dx, DRAWER_WIDTH)}px)`;
    },
    onRelease: (dx, velocity) => {
      settle();
      if (releasesSwipe({ dx, width: DRAWER_WIDTH * 1.5, velocity })) open.current();
    },
    onCancel: settle,
  });
}

/**
 * The tools' count, like a browser's tabs (mobile 3): the panel's icon and how many tools are
 * open, readable at a glance. At 0 it opens the tools sheet, otherwise it goes back to the last
 * tool.
 */
function ToolsCounter({ count, onOpen }: { count: number; onOpen: () => void }) {
  return (
    <button
      // With none open, its icon alone: a "0" read as a count of something (L9).
      aria-label={count === 0 ? 'Tools' : `${count} open ${count === 1 ? 'tool' : 'tools'}`}
      className="flex h-11 min-w-11 shrink-0 items-center justify-center rounded-control px-1 active:bg-control"
      onClick={onOpen}
      type="button"
    >
      <span className="type-meta flex h-7 items-center gap-1 rounded-control bg-control px-2 font-semibold text-foreground tabular-nums">
        <PanelRight aria-hidden className="size-4" />
        {count > 0 && count}
      </span>
    </button>
  );
}

/** A round button of the phone's top bar (mobile 1): the drawer, the bell, search. */
const BAR_BUTTON =
  'relative flex size-11 shrink-0 items-center justify-center rounded-control active:bg-control';

/**
 * The new conversation's top bar (mobile 1): the drawer (an amber dot when something waits on
 * the user), then the bell (its unread count) and search, which open their screens.
 */
function NewTaskBar({
  drawerAttention,
  onOpenDrawer,
  onOpenNotifications,
  onOpenSearch,
  tools,
}: {
  drawerAttention: boolean;
  onOpenDrawer: () => void;
  onOpenNotifications: () => void;
  onOpenSearch: () => void;
  tools: ReactNode;
}) {
  const unread = useNotifications()?.unread ?? 0;
  return (
    <header
      className="relative flex h-16 shrink-0 items-center gap-1 bg-background px-3 pt-2"
      data-testid="mobile-conversation-header"
    >
      <button
        aria-label={drawerAttention ? 'Open the drawer, something waits on you' : 'Open the drawer'}
        className={cn(BAR_BUTTON, 'bg-control/70')}
        onClick={onOpenDrawer}
        type="button"
      >
        <Menu className="size-5" />
        {drawerAttention && (
          <span className="absolute top-1.5 right-1.5 size-2.5 rounded-full bg-attention ring-2 ring-background" />
        )}
      </button>
      <h1 className="sr-only">New conversation</h1>
      <span className="flex-1" />
      {tools}
      <button
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className={BAR_BUTTON}
        onClick={onOpenNotifications}
        type="button"
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span className="type-caption absolute top-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 font-semibold text-background tabular-nums">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      <button aria-label="Search" className={BAR_BUTTON} onClick={onOpenSearch} type="button">
        <Search className="size-5" />
      </button>
    </header>
  );
}

/**
 * The conversation's line under its title (mobile 3): amber "Waits on you" while it waits on
 * the user, else its repository and branch (the branch opens its sheet, 42).
 */
function ConversationSubtitle({
  subtitle,
  waiting,
  cloud,
}: {
  subtitle: string | null;
  waiting: boolean;
  cloud: boolean;
}) {
  if (waiting)
    return (
      <p className="type-caption truncate font-medium text-attention leading-4">Waits on you</p>
    );
  if (subtitle === null) return null;
  return (
    <div className="flex min-w-0 items-center gap-1 [&_[data-branch-chip]]:px-0 [&_[data-branch-chip]]:font-sans [&_[data-branch-chip]_svg]:hidden">
      <p className="type-caption flex min-w-0 shrink items-center gap-1 truncate text-muted-foreground leading-4">
        {cloud && <Cloud aria-hidden className="size-3 shrink-0" />}
        <span className="truncate">{subtitle}</span>
      </p>
      <span aria-hidden className="type-caption shrink-0 text-muted-foreground leading-4">
        ·
      </span>
      {/* Its line is a caption's height; its chip answers a finger's (44 px, M17). */}
      <span className="-my-3.5 flex min-w-0 shrink items-center [&_[data-branch-chip]]:min-h-11">
        <ConversationBranchChip compact />
      </span>
    </div>
  );
}

/**
 * The phone's conversation (the app's start screen, a new conversation first): its chat, and
 * beside it, a swipe left or the tools counter away, its side panel (review, pull request,
 * files, browser, apps…). A new conversation has the app's top bar (mobile 1); a conversation's
 * header (mobile 3) has ← (to the drawer, or a child's parent, 47), the title over its
 * repository and branch (or "Waits on you"), its usage, its Summary page (36), the tools
 * counter and the ⋮ menu (4); on a tool, "← Name ▾ counter" (21). The panel's sheet (7) lists its
 * open tabs and every tool. Both stay mounted (a terminal or a browser keeps running while the
 * chat shows).
 */
export function MobileConversation({
  open,
  title,
  subtitle,
  projectHeading = null,
  started,
  conversation,
  sidePanel,
  header,
  onOpenDrawer,
  onOpenNotifications = () => undefined,
  onOpenSearch = () => undefined,
  onOpenSummary,
  drawerAttention,
}: {
  open: boolean;
  title: string;
  subtitle: string | null;
  /** A project's conversation (mobile 2d, lot 6): its colour and name, then its members. */
  projectHeading?: { title: ReactNode; subtitle: string } | null;
  /** It has messages: a new task shows its composer alone. */
  started: boolean;
  conversation: ReactNode;
  sidePanel: ReactNode;
  /** What the desktop's header shows: the usage and the ⋮ menu's actions. */
  header?: ConversationHeaderProps | undefined;
  onOpenDrawer: () => void;
  onOpenNotifications?: () => void;
  onOpenSearch?: () => void;
  /** Pushes the conversation's Summary page (36). */
  onOpenSummary?: (() => void) | undefined;
  /** Something waits on the user: an amber dot on the drawer's button. */
  drawerAttention: boolean;
}) {
  const screenRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigation = useShellNavigation();
  const waitingThreads = useSidebarWaiting();
  const opener = useConversationOpener();
  const summary = useThreadSummaryRender();
  const sidePanelOpen = useDesktopShellStore((state) => state.sidePanelOpen);
  const chatId = useDesktopShellStore((state) => state.chatId);
  const executionMode = useDesktopShellStore((state) => state.executionMode);
  const tabCount = useDesktopShellStore(
    (state) => state.panelTabs.tabs.filter((tab) => tab.view !== 'new-tab').length,
  );
  const setSidePanelOpen = useDesktopShellStore((state) => state.setSidePanelOpen);
  const setModelMenuOpen = useDesktopShellStore((state) => state.setModelMenuOpen);
  const setLocalHandoffOpen = useDesktopShellStore((state) => state.setLocalHandoffOpen);
  const child = useChildConversation(started ? chatId : null);
  const [tabsOpen, setTabsOpen] = useState(false);
  const panelAvailable = started || tabCount > 0;
  const home = !started && projectHeading === null;
  const threadId = navigation.activeThreadId !== '' ? navigation.activeThreadId : chatId;
  const thread = navigation.threads.find((candidate) => candidate.id === threadId);
  const waiting = waitingThreads.has(threadId) || thread?.actionRequired === true;
  // A preview's child conversation is the boards' running one (47).
  const running = thread?.loading ?? navigation.preview;
  const usage = header?.usage ?? null;
  // The branch is the subtitle's chip (D45); the phone always offers its computers (45).
  const menu = useConversationMenu({
    actions: {
      onRepositorySetup: subtitle === null ? undefined : header?.actions?.onRepositorySetup,
      onContinueLocally: () => setLocalHandoffOpen(true),
    },
    conversationId: chatId,
    surface: 'mobile',
  });
  // A project's own conversation is the project: its ⋮ is the project's.
  const project = useAssistantOfConversation(started || projectHeading !== null ? chatId : null);
  const projectMenu = useProjectMenu(project, chatId);
  // At 0, the counter opens the sheet of tools; otherwise it goes back to the last one.
  const openTools = () => {
    if (tabCount === 0) setTabsOpen(true);
    else setSidePanelOpen(true);
  };
  const closePanel = () => {
    setTabsOpen(false);
    setSidePanelOpen(false);
  };
  const back = child === null ? onOpenDrawer : () => void opener?.openConversation(child.parentId);
  // Swiped right, the chat goes back home; swiped left, its panel follows the finger in. The
  // panel goes back to the chat from the left edge (its browser and terminal take the rest).
  // An app docked in the conversation is one of its tools: ← and the swipe go back to the chat.
  useSwipeOpenDrawer(screenRef, onOpenDrawer, open && !sidePanelOpen);
  useSwipeReveal(chatRef, panelRef, openTools, open && !sidePanelOpen && tabCount > 0);
  useSwipeBack(panelRef, closePanel, open && sidePanelOpen);
  // The sheet's choices close it (they are the panel's own buttons) and show the panel.
  const [list, setList] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (list === null) return;
    const close = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-panel-tab-choice]')) {
        setTabsOpen(false);
        setSidePanelOpen(true);
      }
    };
    list.addEventListener('click', close);
    return () => list.removeEventListener('click', close);
  }, [list, setSidePanelOpen]);
  useEffect(() => {
    if (!tabsOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTabsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tabsOpen]);
  const counter = panelAvailable ? (
    <ToolsCounter count={tabCount} onOpen={sidePanelOpen ? () => setTabsOpen(true) : openTools} />
  ) : null;

  return (
    <div
      aria-hidden={!open || undefined}
      className={cn(
        'absolute inset-0 z-20 flex flex-col overflow-clip bg-background shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.2,0.75,0.3,1)]',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
      inert={!open || undefined}
      ref={screenRef}
    >
      {home && !sidePanelOpen ? (
        <NewTaskBar
          drawerAttention={drawerAttention}
          onOpenDrawer={onOpenDrawer}
          onOpenNotifications={onOpenNotifications}
          onOpenSearch={onOpenSearch}
          tools={counter}
        />
      ) : (
        <header
          // Every button of the bar is a finger's 44 × 44, side by side without overlapping
          // (the ⋮ comes from the conversation menu and is sized here).
          className="relative flex h-14 shrink-0 items-center bg-background px-1 [&>button]:min-h-11 [&>button]:min-w-11"
          data-testid="mobile-conversation-header"
        >
          <Button
            aria-label={
              sidePanelOpen
                ? 'Back to the chat'
                : child !== null
                  ? `Back to ${child.parentTitle}`
                  : 'Open the drawer'
            }
            className="size-11"
            onClick={sidePanelOpen ? closePanel : back}
            size="icon"
            variant="ghost"
          >
            <ArrowLeft className="size-5" />
          </Button>
          <div className={cn('min-w-0 flex-1', sidePanelOpen && 'text-center')}>
            {/* Kept mounted (hidden with the chat): the panel portals its tab's name in. */}
            <button
              aria-expanded={tabsOpen}
              aria-label="Panel tabs"
              className={cn(
                'mx-auto max-w-full items-center justify-center gap-1 rounded-control px-2 py-1 font-semibold active:bg-control',
                sidePanelOpen ? 'flex' : 'hidden',
              )}
              onClick={() => setTabsOpen(true)}
              type="button"
            >
              <span className="truncate" ref={panelTitleSlot.set} />
              <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
            </button>
            {!sidePanelOpen && (
              <>
                {child !== null && (
                  <span
                    className="flex min-w-0 [&_button]:gap-1 [&_button]:text-xs [&_button]:leading-4"
                    data-child-header
                  >
                    <ParentLink
                      child={child}
                      onOpen={() => void opener?.openConversation(child.parentId)}
                    />
                  </span>
                )}
                <p className="truncate type-card-title leading-5">
                  {projectHeading?.title ?? title}
                </p>
                {projectHeading !== null ? (
                  <p className="type-caption truncate text-muted-foreground leading-4">
                    {projectHeading.subtitle}
                  </p>
                ) : (
                  child === null && (
                    <ConversationSubtitle
                      cloud={executionMode === 'cloud'}
                      subtitle={subtitle}
                      waiting={waiting}
                    />
                  )
                )}
              </>
            )}
          </div>
          {!sidePanelOpen && child !== null && running && (
            <span
              className="type-meta mr-1 inline-flex h-7 shrink-0 items-center gap-1.5 rounded-control bg-running/15 px-2.5 font-medium text-running"
              data-running-chip
            >
              <LoaderCircle aria-hidden className="size-3.5 animate-spin" />
              Running
            </span>
          )}
          {!sidePanelOpen && child === null && started && usage !== null && (
            <button
              aria-label={`Context usage: ${Math.round(usageRatio(usage) * 100)}%`}
              // A 320 px screen gives its room to the title: the model's sheet has the usage too.
              className="flex size-11 shrink-0 items-center justify-center rounded-control active:bg-control max-[359px]:hidden"
              onClick={() => setModelMenuOpen(true)}
              type="button"
            >
              <UsageRingGlyph className="size-5" ratio={usageRatio(usage)} />
            </button>
          )}
          {!sidePanelOpen &&
            child === null &&
            started &&
            summary !== null &&
            onOpenSummary !== undefined && (
              <Button
                aria-label="Summary"
                className="size-11"
                onClick={onOpenSummary}
                size="icon"
                variant="ghost"
              >
                <LayoutGrid className="size-5" />
              </Button>
            )}
          {(sidePanelOpen || child === null) &&
            (counter ?? <span aria-hidden className="size-11 shrink-0" />)}
          {!sidePanelOpen && started && (
            <>
              <ConversationMenu
                entries={project === null ? menu.entries : projectMenu.entries}
                surface="mobile"
                title={project?.name ?? title}
              />
              {project === null ? menu.dialogs : projectMenu.dialogs}
            </>
          )}
        </header>
      )}
      <div className="relative flex min-h-0 flex-1 flex-col" ref={chatRef}>
        <div className="flex min-h-0 flex-1 flex-col">{conversation}</div>
        <div
          aria-hidden={!sidePanelOpen || undefined}
          className={cn(
            'absolute inset-0 flex bg-background transition-transform duration-300 ease-[cubic-bezier(0.2,0.75,0.3,1)]',
            sidePanelOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full',
          )}
          inert={!sidePanelOpen || undefined}
          ref={panelRef}
        >
          {sidePanel}
        </div>
      </div>
      {/* Mounted while closed: the panel portals its tabs in as soon as it renders. */}
      <div className={cn('fixed inset-0 z-modal', !tabsOpen && 'hidden')}>
        <button
          aria-label="Close the tools sheet"
          className="absolute inset-0 bg-overlay-backdrop"
          onClick={() => setTabsOpen(false)}
          type="button"
        />
        <div
          aria-label="Tools"
          aria-modal
          className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-window bg-popover pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          // A sheet (a history entry the phone's back closes) only while it shows.
          data-sheet={tabsOpen || undefined}
          data-touch-sheet="off"
          role="dialog"
        >
          <div className="mx-auto mb-1 h-1 w-9 rounded-full bg-muted-foreground/45" />
          <MobileSheetHeader
            closeLabel="Close the tools sheet"
            onClose={() => setTabsOpen(false)}
            title="Tools"
          />
          <div
            ref={(element) => {
              panelTabListSlot.set(element);
              setList(element);
            }}
          />
        </div>
      </div>
    </div>
  );
}
